import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import styled from "styled-components";
import {
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  Typography,
  Divider,
  DatePicker,
  Skeleton,
  Spin,
  Checkbox,
  Tooltip
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  FileImageOutlined,
  HourglassOutlined,
  Loading3QuartersOutlined,
  RightOutlined,
  SmileOutlined,
  InfoCircleOutlined,
  PictureOutlined
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import debounce from "lodash/debounce";
import maxBy from "lodash/maxBy";

import { useApi, ApiNames } from "../../hooks/api";
import EeReviewBody from "./EeReviewBody";
import { TaskStatePanel } from "./TaskStatePanel";
import { useFetch } from "../../hooks/fetch";
import { searchRxnormDrugsAPI, getManagePatientDetails, setPatientDetailsAPI } from "../../api/api";
import { TERM_TYPES } from "../../constant/drugs";
import { Routes } from "../../constant/routes";
import { DATE_FORMAT, DATE_DATA_FORMAT } from "../../utils/date";
import {
  agentStepEvents,
  currentAgentSteps,
  findConsentStateCarrier,
  hypercareReasonLabel,
  RESULT_ICON
} from "../../component/Patient/AgenticRuns/runStatuses";
import { formatMedicationLine, patientLabel } from "./taskHelpers";
import { HITL_TASK_TYPES, AGENTIC_STEP_NAMES } from "../../constant/agenticPlatform";
import { ActionButton, DangerActionButton, SecondaryActionButton } from "./taskStyles";
import { EmrScreenshotsViewer } from "./EmrScreenshotsViewer";
import { selectIsPhiAllowed } from "../../store/selector";
// Same email + phone units Manage Patient Details uses, so the consent
// contact confirm validates + masks identically: `validateEmail` (the shared
// email-format validator), `phoneNumberLengthRegEx` (10-digit pattern), and
// `formatPhoneNumberWhileTyping` (the live "(123) 456-7890" mask).
import { validateEmail, phoneNumberLengthRegEx } from "../../utils/formValidation";
import { formatPhoneNumberWhileTyping } from "../../utils/formaters";
import { getPurePhoneNumber } from "../../utils/misc";
import { GRAY_200, GRAY_500, GRAY_600, GRAY_700, PRIMARY_600, SUCCESS_500, ERROR_500 } from "../../constant/colors";

const { Text } = Typography;

/**
 * Six seconds rather than antd's default three — the resolved toast carries a
 * link to the patient, and a link nobody has time to click is just decoration.
 */
const RESOLVED_TOAST_DURATION_SECONDS = 6;

/**
 * Plan-market options, mirroring the platform's `PlanMarket` enum string
 * values verbatim (the navigator picks one per insurance row instead of
 * typing free text). The FNC's shared `PLAN_MARKETS` constant doesn't line
 * up — it lacks `strictly_commercial` and carries an `uncovered` value the
 * platform doesn't accept — so the platform-contract list lives here.
 */
const PLAN_MARKET_VALUES = Object.freeze([
  "commercial",
  "strictly_commercial",
  "va",
  "tricare",
  "medicare_part_a",
  "medicare_part_b",
  "medicare_part_a_and_b",
  "medigap",
  "medicare_advantage",
  "medicare_part_d",
  "medicaid",
  "uninsured",
  "unknown",
  "assistance"
]);

// Resolve the patient's drugs from the existing RxNorm search. Each result is
// a full `rxnormDrug` object ({ id, tmDisplayName, tty, bnName, ... }); the
// selected ones are sent verbatim to the platform.
//
// Only BRAND (BN) and INGREDIENT (PIN) results are pickable — mirroring the
// "find assistance program" drug selector (common-client `MultiSelectDrugSearch`).
// Eligibility matches a PAP's drug at its `matchedDrugTTY` (BN / PIN); a
// product/form-level pick (SBDF / SCD / SBD / …) carries a tty that can't line
// up against that, so restricting the search keeps the manual fill to the
// levels eligibility actually matches on.
const BRAND_AND_INGREDIENT_TTYS = [TERM_TYPES.BN, TERM_TYPES.PIN];

const searchRxnormDrugs = async (searchTerm) => {
  const response = await searchRxnormDrugsAPI({ text: searchTerm });
  const drugs = response?.data?.rxnormDrugs || [];
  return drugs.filter((drug) => BRAND_AND_INGREDIENT_TTYS.includes(drug.tty));
};

// Single-select, searchable drug field — looks like the plan-market dropdown
// (a single chosen value, no tags) but with an async RxNorm search. `value` is
// the full `rxnormDrug` object; the debounced search fills the options and
// `onChange` hands back the picked drug (or null on clear). One per med row.
const DrugSearchSelect = ({ value, onChange, placeholder, ...rest }) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [searchError, setSearchError] = useState(false);

  // Memoized so the debounce timer survives re-renders.
  const runSearch = useMemo(
    () =>
      debounce(async (term) => {
        if (!term || term.trim().length < 2) {
          setOptions([]);
          setSearchError(false);
          return;
        }
        setFetching(true);
        setSearchError(false);
        try {
          const drugs = await searchRxnormDrugs(term);
          setOptions(drugs.map((drug) => ({ label: drug.tmDisplayName, value: drug.ttyCui, drug })));
        } catch (error) {
          // A failed search must not look like "no matches" — clear the options
          // and flag the error so the navigator retries instead of assuming the
          // drug doesn't exist.
          setOptions([]);
          setSearchError(true);
        } finally {
          setFetching(false);
        }
      }, 400),
    []
  );

  return (
    <Select
      {...rest}
      showSearch
      allowClear
      labelInValue
      filterOption={false}
      style={{ width: "100%" }}
      placeholder={placeholder}
      value={value ? { value: value.ttyCui, label: value.tmDisplayName } : undefined}
      onSearch={runSearch}
      onChange={(_selected, option) => onChange(option?.drug ?? null)}
      notFoundContent={
        fetching ? (
          <Spin size="small" />
        ) : searchError ? (
          <Typography.Text type="danger">
            {t("agentic_board.emr_fill.drug_search_failed", "Couldn't search — try again")}
          </Typography.Text>
        ) : null
      }
      options={options}
    />
  );
};

// Humanize a camelCase contact-field key for labels / chips.
const contactFieldLabel = (field, t) =>
  ({
    dob: t("agentic_board.contact.dob"),
    email: t("agentic_board.contact.email"),
    phoneNumber: t("agentic_board.contact.phone")
  }[field] || field);

// The "at least one channel" gate is a hidden input, but antd still reserves a
// ~32px control row for it — opening a dead gap above the "Link will be sent
// via" line. Collapse that row to 0 (the inline error still renders via the
// explain row beneath it).
const ChannelGateItem = styled(Form.Item)`
  .ant-form-item-control-input {
    min-height: 0;
  }
`;

// ─── emr_fill body ─────────────────────────────────────────────────────────
// Insurance rows + a drug picker. Insurances stay as raw narrowed fields plus
// a REQUIRED plan-market pick per row (the navigator must classify each row's
// coverage — the backend uses it directly instead of pattern-matching raw
// strings). Medications are resolved from the RxNorm search so the platform
// gets the picked drug's id (it re-fetches the live one) rather than names. Medications
// mirror the insurances rows: "Add medication" appends a row with a searchable
// single drug picker + optional start date + remove ✕. The rows live in the
// parent (lifted) since the picker hands back full drug objects, not a plain
// form value. Each medication row carries an optional treatment window (start
// + end) so a manual fill can record the same range extraction produces.

const EmrFillBody = ({
  form,
  medicationRows,
  onAddMedicationRow,
  onMedicationDrugChange,
  onMedicationStartDateChange,
  onMedicationEndDateChange,
  onRemoveMedicationRow
}) => {
  const { t } = useTranslation();

  return (
    <Form form={form} layout="vertical" initialValues={{ insurances: [] }}>
      <Text type="secondary" style={{ display: "block", marginBottom: 12, fontSize: 13 }}>
        {t("agentic_board.emr_fill.caption")}
      </Text>

      <Text strong style={{ display: "block", marginBottom: 8, color: GRAY_700 }}>
        {t("agentic_board.emr_fill.insurances")}
      </Text>
      <Form.List name="insurances">
        {(fields, { add, remove }) => (
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            {fields.map(({ key, name, ...rest }) => (
              <div
                key={key}
                style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}
                data-testid={`agentic-emr-fill-insurance-${name}`}
              >
                <Form.Item {...rest} name={[name, "planName"]} style={{ flex: 1, minWidth: 160, marginBottom: 0 }}>
                  <Input placeholder={t("agentic_board.emr_fill.plan_name")} />
                </Form.Item>
                <Form.Item {...rest} name={[name, "planType"]} style={{ flex: 1, minWidth: 120, marginBottom: 0 }}>
                  <Input placeholder={t("agentic_board.emr_fill.plan_type")} />
                </Form.Item>
                <Form.Item
                  {...rest}
                  name={[name, "financialClass"]}
                  style={{ flex: 1, minWidth: 120, marginBottom: 0 }}
                >
                  <Input placeholder={t("agentic_board.emr_fill.financial_class")} />
                </Form.Item>
                <Form.Item {...rest} name={[name, "groupName"]} style={{ flex: 1, minWidth: 120, marginBottom: 0 }}>
                  <Input placeholder={t("agentic_board.emr_fill.group_name")} />
                </Form.Item>
                <Form.Item {...rest} name={[name, "group"]} style={{ flex: 1, minWidth: 100, marginBottom: 0 }}>
                  <Input placeholder={t("agentic_board.emr_fill.group")} />
                </Form.Item>
                <Form.Item
                  {...rest}
                  name={[name, "planMarket"]}
                  style={{ flex: 1, minWidth: 160, marginBottom: 0 }}
                  rules={[
                    {
                      required: true,
                      message: t("agentic_board.emr_fill.plan_market_required")
                    }
                  ]}
                >
                  <Select
                    placeholder={t("agentic_board.emr_fill.plan_market_placeholder")}
                    data-testid={`agentic-emr-fill-plan-market-${name}`}
                    options={PLAN_MARKET_VALUES.map((value) => ({
                      value,
                      label: t(`agentic_board.emr_fill.plan_market_options.${value}`)
                    }))}
                  />
                </Form.Item>
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => remove(name)}
                  aria-label={t("agentic_board.emr_fill.remove_row")}
                />
              </div>
            ))}
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => add({})}
              style={{ alignSelf: "flex-start" }}
              data-testid="agentic-emr-fill-add-insurance"
            >
              {t("agentic_board.emr_fill.add_insurance")}
            </Button>
          </Space>
        )}
      </Form.List>

      <Divider style={{ margin: "16px 0" }} />

      <Text strong style={{ display: "block", marginBottom: 8, color: GRAY_700 }}>
        {t("agentic_board.emr_fill.medications")}
      </Text>
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        {medicationRows.map((row) => (
          <div
            key={row.key}
            style={{ display: "flex", gap: 8, alignItems: "flex-start" }}
            data-testid={`agentic-emr-fill-med-row-${row.key}`}
          >
            <div style={{ flex: 1, minWidth: 200 }}>
              <DrugSearchSelect
                id={`agentic-emr-fill-drug-${row.key}`}
                value={row.drug}
                placeholder={t("agentic_board.emr_fill.medications_placeholder")}
                onChange={(drug) => onMedicationDrugChange(row.key, drug)}
                data-testid={`agentic-emr-fill-drug-${row.key}`}
              />
            </div>
            {/* Treatment window. Either end may be left blank — the platform
                stores what it gets, and the agent view renders "since <start>"
                when only a start is known. The two `disabledDate` guards make
                an inverted window unpickable rather than a submit-time error. */}
            <DatePicker
              value={row.startDate || null}
              onChange={(date) => onMedicationStartDateChange(row.key, date)}
              disabledDate={(current) => (row.endDate ? current && current > row.endDate : false)}
              format={DATE_FORMAT}
              placeholder={t("agentic_board.emr_fill.med_start_date")}
              style={{ width: 180 }}
              data-testid={`agentic-emr-fill-med-start-date-${row.key}`}
            />
            <DatePicker
              value={row.endDate || null}
              onChange={(date) => onMedicationEndDateChange(row.key, date)}
              disabledDate={(current) => (row.startDate ? current && current < row.startDate : false)}
              format={DATE_FORMAT}
              placeholder={t("agentic_board.emr_fill.med_end_date")}
              style={{ width: 180 }}
              data-testid={`agentic-emr-fill-med-end-date-${row.key}`}
            />
            <Button
              type="text"
              icon={<DeleteOutlined />}
              onClick={() => onRemoveMedicationRow(row.key)}
              aria-label={t("agentic_board.emr_fill.remove_row")}
              data-testid={`agentic-emr-fill-med-remove-${row.key}`}
            />
          </div>
        ))}
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={onAddMedicationRow}
          style={{ alignSelf: "flex-start" }}
          data-testid="agentic-emr-fill-add-medication"
        >
          {t("agentic_board.emr_fill.add_medication")}
        </Button>
      </Space>
    </Form>
  );
};

// ─── consent_decide body ────────────────────────────────────────────────────
// The consent HITL is the sole navigator *decision* the board opens (the only
// other modal type is the emr_fill data-entry fallback). The body shows the
// navigator everything needed to choose Send vs Don't-send without leaving the
// board:
//   1. a humanized consent state + a one-line "what Send does here" sentence,
//   2. a compact summary of the run's pipeline steps (its own fetch),
//   3. a link to the patient page,
//   4. the patient's contact details — dob/email/phone read LIVE from FN when
//      the modal opens (the same patient-edit details Manage Patient Details
//      reads), pre-filled + editable, with the live channel line ("Link will be
//      sent via: …") derived from whichever of email/phone is present,
//   5. a "Send with FPL" toggle (defaults on when the agent found Medicare),
//      whose value rides along on the approve signal.
// The Send / Don't-send footer lives on the modal shell (uniform for every
// state); this body only renders the decision context + the contact fields.
// eConsent needs a date of birth and at least one of email/phone — the body
// reads them live, gates Send on them client-side, then saves any edits to the
// FN patient before signaling approve (the platform re-validates on resume).

// The eight consent states the pause can carry on `review.payload.consentOutcome`
// (platform `ConsentReason` — the snake_case value, e.g. `eligible_to_send`). Each resolves to a
// welcoming { title, body } copy pair (see `consentStateCopy`) — never the raw
// enum. Sending is allowed in EVERY state (the footer is uniform); the body copy
// frames each state so the navigator can make an informed call (e.g. re-sending
// over an active consent, or while a link is already in flight).
const CONSENT_OUTCOME_KEYS = Object.freeze({
  ELIGIBLE_TO_SEND: "eligible_to_send",
  PATIENT_HAS_ACTIVE_CONSENT: "patient_has_active_consent",
  CONSENT_LINK_IN_PROGRESS: "consent_link_in_progress",
  CONSENT_LINK_EXPIRED: "consent_link_expired",
  CONSENT_LINK_REVOKED: "consent_link_revoked",
  CONSENT_REVOKED: "consent_revoked",
  CONSENT_EXPIRED: "consent_expired",
  CONTACT_INFO_MISSING: "contact_info_missing"
});

// The "good news" states — a clean eligible patient (or one that's eligible and
// just needs contact details). These get the warm green panel + smile icon; every
// other (edge) state gets the calm neutral info panel. By snake_case key so the
// check runs after `consentOutcomeKey` normalizes the outcome (case-insensitive).
const GOOD_NEWS_OUTCOME_KEYS = Object.freeze(["eligible_to_send", "contact_info_missing"]);
const isGoodNewsOutcome = (outcomeKey) => GOOD_NEWS_OUTCOME_KEYS.includes(outcomeKey);

// Resolve the snake_case state key for an outcome. The board carries the state as
// the ConsentReason VALUE (`eligible_to_send`); we also tolerate the UPPERCASE enum
// key (`ELIGIBLE_TO_SEND`) by lower-casing and matching the known value set.
// Unknown / missing outcomes fall back to "unknown" so the body renders the generic
// copy pair rather than showing a raw enum.
const CONSENT_OUTCOME_VALUES = new Set(Object.values(CONSENT_OUTCOME_KEYS));
const consentOutcomeKey = (outcome) => {
  const key = outcome ? String(outcome).toLowerCase() : "";
  return CONSENT_OUTCOME_VALUES.has(key) ? key : "unknown";
};

// Welcoming { title, body } copy for a consent outcome — human, PHI-free ("this
// patient", never a name). Keyed by the normalized state; unmapped codes get the
// generic `unknown` pair so a raw enum never surfaces.
const consentStateCopy = (outcome, t) => {
  const key = consentOutcomeKey(outcome);
  return {
    title: t(`agentic_board.consent.states.${key}.title`),
    body: t(`agentic_board.consent.states.${key}.body`)
  };
};

// ── Workflow-steps summary ──
// A condensed read of the run's audit trail — one row per pipeline step
// (EMR · Financial assistance · Remaining OOP · Consent). Each row shows the
// stage's outcome/result plus a muted secondary line surfacing WHAT the agent
// found from that stage's payload (coverage + active meds, eligible programs,
// applied OOP). Deliberately NOT the full AgenticRunsModal (no per-step expand,
// no raw payload JSON) — just enough for the navigator to see how the run
// reached the consent gate. Fetches the same latest-run-details payload the
// audit modal uses (AgenticLatestRunDetails).

// The four pipeline stages, in execution order, mapped to the audit
// `step.stepName`(s) that represent each. EMR has two possible step names —
// `emr` (parsed data) or the earlier `emr_gathering` wait — so the summary
// matches whichever the run recorded.
const SUMMARY_STAGES = Object.freeze([
  {
    key: "emr",
    stepNames: [AGENTIC_STEP_NAMES.EMR_DATA_PARSED, AGENTIC_STEP_NAMES.EMR_GATHERING],
    labelKey: "agentic_board.steps_summary.emr",
    fallback: "EMR"
  },
  {
    key: "financial_assistance",
    stepNames: [AGENTIC_STEP_NAMES.FINANCIAL_ASSISTANCE_ELIGIBILITY],
    labelKey: "agentic_board.steps_summary.financial_assistance",
    fallback: "Financial assistance"
  },
  {
    key: "remaining_oop",
    stepNames: [AGENTIC_STEP_NAMES.REMAINING_OOP_CALCULATION],
    labelKey: "agentic_board.steps_summary.remaining_oop",
    fallback: "Remaining OOP"
  },
  {
    key: "consent",
    stepNames: [AGENTIC_STEP_NAMES.CONSENT_STATE_CHECK],
    labelKey: "agentic_board.steps_summary.consent",
    fallback: "Consent"
  }
]);

// The ee_review view of the same run. The conductor's stages are how the run
// REACHED the enrollment, so they all belong here; the last two are the
// enrollment child's own steps, and `ee_human_review` is the pause the navigator
// is answering right now.
export const EE_SUMMARY_STAGES = Object.freeze([
  ...SUMMARY_STAGES,
  {
    key: "ee_config_check",
    stepNames: [AGENTIC_STEP_NAMES.EE_CONFIG_CHECK],
    labelKey: "agentic_board.steps_summary.ee_config_check",
    fallback: "Express enrollment available"
  },
  {
    key: "ee_human_review",
    stepNames: [AGENTIC_STEP_NAMES.EE_HUMAN_REVIEW],
    labelKey: "agentic_board.steps_summary.ee_human_review",
    fallback: "Your review"
  }
]);

// Per-result icon + color for the summary row's status dot. `passed`/`failed`/
// `skipped` mirror the audit-step result enum; a null result (step still
// in-flight) shows the running spinner, and an absent step shows the muted
// hourglass "pending" marker.
const SUMMARY_RESULT_VISUAL = {
  passed: { Icon: RESULT_ICON.passed, color: SUCCESS_500 },
  failed: { Icon: RESULT_ICON.failed, color: ERROR_500 },
  skipped: { Icon: RESULT_ICON.skipped, color: GRAY_500 }
};
const SUMMARY_RUNNING_VISUAL = { Icon: Loading3QuartersOutlined, color: PRIMARY_600, spin: true };
const SUMMARY_PENDING_VISUAL = { Icon: HourglassOutlined, color: GRAY_500 };

const summaryVisualForStep = (step) => {
  if (!step) return SUMMARY_PENDING_VISUAL;
  if (step.result == null) return SUMMARY_RUNNING_VISUAL;
  return SUMMARY_RESULT_VISUAL[step.result] || SUMMARY_RESULT_VISUAL.skipped;
};

// Humanize the Consent step's ConsentReason (`step.outcome`, e.g.
// `eligible_to_send`) into a plain situation phrase — "No consent sent yet",
// "Sent link expired", "Active consent on file", … An unmapped/missing code
// falls back to the raw outcome so a state shift never hides behind a blank.
const consentSituationLine = (step, t) => {
  const outcome = step?.outcome;
  if (!outcome) return null;
  return t(`agentic_board.steps_summary.consent_situation.${outcome}`);
};

// The Consent row's two detail lines (situation first, then readiness):
//   • situation — the humanized ConsentReason (`step.outcome`),
//   • readiness — "Ready to send" when the patient has at least one usable
//     contact channel (`step.payload.availableContactChannels`); otherwise the
//     "Add contact details to send." prompt. Sending is allowed in essentially
//     every state, so the line only flips to the add-contact prompt when no
//     channel is on file.
// Returns an array of lines (the row renders each as its own muted sub-line),
// dropping the situation line when there's no consent step yet.
const consentStageDetail = (step, t) => {
  const hasChannel = step?.payload?.availableContactChannels?.length >= 1;
  const readiness = hasChannel
    ? t("agentic_board.steps_summary.ready_to_send")
    : t("agentic_board.steps_summary.add_contact_to_send");
  return [consentSituationLine(step, t), readiness].filter(Boolean);
};

// ── Per-stage secondary detail ──
// The muted line(s) under each stage row — the ONLY thing a row carries besides
// the green check + name (there's no right-hand status column). The data stages
// (EMR / financial assistance / remaining OOP) each surface WHAT the agent found
// from that stage's payload (the same audit JSON `WorkflowStepsSummary` fetched)
// as a single line; the Consent stage carries TWO (its situation + readiness,
// see `consentStageDetail` above). A data-stage builder returns a short string
// or null; consent returns an array of lines. Kept terse.

// EMR detail: coverage type(s) + active medication(s), ONE per line (not a
// run-on sentence). Coverage reuses the emr_fill plan-market labels (the
// payload's `planMarket` values are the same PlanMarket enum strings the picker
// uses); each med lists the agent-matched active drug name with its treatment
// window when present — a range where the source has both ends, "since" where
// it has only a start. Rendered as:
//   Strictly commercial
//   bevacizumab (Avastin) (Apr 7, 2026 - Jun 29, 2026)
//   pembrolizumab (Keytruda) (since Jun 10, 2026)
const emrStageDetail = (step, t) => {
  const payload = step?.payload || {};
  const insurances = Array.isArray(payload.insurances) ? payload.insurances : [];
  const medications = Array.isArray(payload.medications) ? payload.medications : [];

  // Distinct humanized plan markets across the rows, source order preserved.
  const markets = [...new Set(insurances.map((ins) => ins?.planMarket).filter(Boolean))].map((market) =>
    t(`agentic_board.emr_fill.plan_market_options.${market}`)
  );

  // Active meds, each with its treatment window when present. Map the whole row
  // so name + dates stay paired, then drop nameless rows. Shared formatter, so
  // this line and the patient page's runs modal read identically.
  const meds = medications.filter((med) => med?.name).map((med) => formatMedicationLine(med, t));

  // One line per fact so the row reads as a list: coverage first (markets on a
  // single line — usually just one), then each active medication on its own line.
  const lines = [];
  if (markets.length > 0) lines.push(markets.join(", "));
  lines.push(...meds);
  return lines.length > 0 ? lines : null;
};

// Financial-assistance detail: a line per hypercare reason the agent checked
// (reason + eligible / not-eligible verdict), plus — for an eligible COMMERCIAL
// (copay-card) reason — one ADDITIONAL line per matched program below it (each
// criterion item's label is already "Program (Drug)", e.g. "Genentech Co-pay
// (Avastin)"). Broken across lines rather than a comma-run on the verdict line.
// The per-criterion drug breakdown stays in the full run modal, not here.
const financialAssistanceStageDetail = (step, t) => {
  const reasonChecks = step?.payload?.reasonChecks;
  if (!Array.isArray(reasonChecks) || reasonChecks.length === 0) return null;

  return reasonChecks.flatMap((rc) => {
    const verdict = rc.eligible
      ? t("agentic_board.steps_summary.fa_eligible")
      : t("agentic_board.steps_summary.fa_not_eligible");
    const lines = [`${hypercareReasonLabel(rc.reason)} — ${verdict}`];

    // Copay-card-eligible commercial reason → one line per matched program,
    // instead of a comma-run appended to the verdict line.
    if (rc.eligible && rc.family === "commercial") {
      const programs = (rc.criteria || [])
        .flatMap((c) => c.items || [])
        .filter((it) => it.passed)
        .map((it) => it.label);
      lines.push(...programs);
    }
    return lines;
  });
};

// Remaining-OOP detail: ALWAYS rendered so the navigator sees each active
// coverage's remaining out-of-pocket figure on every run.
//   - amounts + threshold → "Remaining OOP $1,200 · Threshold $5,000"
//   - amounts only        → "Remaining OOP $1,200"   (threshold unknown)
//   - no known amounts    → "N/A"                    (FN had no coverage figures)
// Dollar amounts are comma-grouped (toLocaleString) — `0` is a real value,
// so only null/undefined entries are dropped.
const remainingOopStageDetail = (step, t) => {
  const { remainingOops, threshold } = step?.payload || {};
  const known = (Array.isArray(remainingOops) ? remainingOops : []).filter((v) => v != null);
  if (known.length === 0) return t("agentic_board.steps_summary.oop_na");
  const amounts = known.map((v) => `$${Number(v).toLocaleString()}`).join(", ");
  if (threshold == null) {
    return t("agentic_board.steps_summary.oop_remaining_no_threshold", { amounts });
  }
  return t("agentic_board.steps_summary.oop_remaining", {
    amounts,
    threshold: Number(threshold).toLocaleString()
  });
};

// Stage → detail builder. Every stage has one: the data stages surface their
// payload findings as a single line; consent returns TWO lines (its situation +
// readiness). A stage without an entry would simply render no secondary line.
const STAGE_DETAIL_BUILDERS = {
  emr: emrStageDetail,
  financial_assistance: financialAssistanceStageDetail,
  remaining_oop: remainingOopStageDetail,
  consent: consentStageDetail
};

// Normalize a stage's detail to an array of lines: the data stages return one
// string (or null), consent returns an array. Drops empties so the row renders
// only real lines.
const stageDetailLines = (stage, step, t) => {
  const builder = STAGE_DETAIL_BUILDERS[stage.key];
  if (!builder) return [];
  const detail = builder(step, t);
  return (Array.isArray(detail) ? detail : [detail]).filter(Boolean);
};

/**
 * The express-enrollment stage, which is the ONE stage with more than one step:
 * the conductor runs an enrollment child per enrollable pap, so each writes its
 * own `ee_config_check`. Listing them is the only way the navigator can see what
 * else the agent is doing for this patient — and which of them THIS modal is
 * reviewing.
 *
 * Program and drug names are joined onto the step payload by FN at read time
 * (`hydrateEnrollmentStepPrograms`); with no names to show, the row degrades to
 * the plain stage line rather than printing pap ids at a human.
 */
const EnrollmentSummaryRow = ({ stage, steps, reviewingPapId, t }) => {
  const named = steps.filter((step) => step?.payload?.programName);
  if (!named.length) return <StepSummaryRow stage={stage} step={maxBy(steps, "id")} t={t} />;

  return (
    <div data-testid="agentic-consent-step-ee_config_check" style={{ padding: "4px 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <RESULT_ICON.passed style={{ color: SUCCESS_500, fontSize: 13, flexShrink: 0 }} />
        <Text style={{ fontSize: 12, color: GRAY_700 }}>{t(stage.labelKey, stage.fallback)}</Text>
      </div>
      {named.map((step) => {
        const { papId, programName, drugName } = step.payload;
        const isReviewing = reviewingPapId != null && papId === reviewingPapId;
        return (
          <Text
            key={papId}
            type="secondary"
            data-testid={`agentic-ee-summary-pap-${papId}`}
            style={{ display: "block", fontSize: 11, marginLeft: 21, lineHeight: 1.4 }}
          >
            {drugName ? `${programName} (${drugName})` : programName}
            {isReviewing && (
              <Text style={{ fontSize: 11, color: PRIMARY_600 }}>
                {` — ${t("agentic_board.ee_review.reviewing_now")}`}
              </Text>
            )}
          </Text>
        );
      })}
    </div>
  );
};

const StepSummaryRow = ({ stage, step, t }) => {
  const { Icon, color, spin } = summaryVisualForStep(step);
  // One or more muted sub-lines (consent carries two: situation + readiness;
  // the data stages carry one). Each renders as its own line under the label.
  const lines = stageDetailLines(stage, step, t);
  return (
    <div data-testid={`agentic-consent-step-${stage.key}`} style={{ padding: "4px 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Icon spin={!!spin} style={{ color, fontSize: 13, flexShrink: 0 }} />
        <Text style={{ fontSize: 12, color: GRAY_700 }}>{t(stage.labelKey, stage.fallback)}</Text>
      </div>
      {lines.map((line, index) => (
        <Text
          // Stable enough: a stage's lines are positional and fixed (situation
          // then readiness), never reordered.
          key={index}
          type="secondary"
          data-testid={`agentic-consent-step-detail-${stage.key}`}
          // Align under the label (icon width + gap) so the detail reads as a
          // sub-line of the stage, not a new row.
          style={{ display: "block", fontSize: 11, marginLeft: 21, lineHeight: 1.4 }}
        >
          {line}
        </Text>
      ))}
    </div>
  );
};

// Fetches the patient's latest run (with steps) and renders one compact row
// per pipeline stage. A HITL task pauses its run mid-flight, and the
// per-patient dedup means that paused run IS the patient's latest — so the
// latest-run-details read is the task's run. Self-contained; degrades quietly:
// a load error / missing run just hides the summary rather than blocking the
// decision.
export const WorkflowStepsSummary = ({
  patientId,
  t,
  extra = null,
  stages = SUMMARY_STAGES,
  reviewingPapId = null
}) => {
  const [fetchRunDetails, run, isFetching] = useApi(ApiNames.AgenticLatestRunDetails);

  // `useApi` returns a fresh `fetchRunDetails` identity each render, so call it
  // via a ref and keep it out of the dep array (adding it would loop). The read
  // is keyed on `patientId` only.
  const fetchRunDetailsRef = useRef(fetchRunDetails);
  fetchRunDetailsRef.current = fetchRunDetails;
  useEffect(() => {
    if (patientId) fetchRunDetailsRef.current(patientId);
  }, [patientId]);

  // `run.steps` is an append-only log — a step that parked and later resolved
  // arrives as several events — so reduce to each step's CURRENT event before any
  // lookup below. `stepForStage` already picked the newest by `id`; this makes the
  // same rule hold for every other read here.
  //
  // Through the SAME two helpers the patient-page modal uses, not `latestStepEvents`
  // directly: `run_resumed` is a restart boundary rather than work the agent did,
  // and a superseded step is one the restarted run never came back to. Reading the
  // log raw here showed both — a restart as a step, and an abandoned wait as its
  // stage's current event.
  //
  // Keyed on `run?.steps`, NOT `run`: `useApi` hands back a fresh `run`
  // identity on every render, so depending on the object recomputed this — and
  // handed every child a new array — on each pass. The log is the only input
  // either helper reads.
  const steps = useMemo(() => currentAgentSteps(agentStepEvents(run)), [run?.steps]);

  // The source-screenshots action only makes sense when the run actually READ
  // screenshots. With EMR data turned off for the site, the same step reads
  // TailorMed's own record and stamps `source: "tm"` — there is nothing to show,
  // so the button would open an empty viewer. Unknown/missing source keeps
  // today's behaviour rather than hiding something that might exist.
  const readFromTmRecord =
    steps.find((step) => step.stepName === AGENTIC_STEP_NAMES.EMR_DATA_PARSED)?.payload?.source === "tm";
  const sourceAction = readFromTmRecord ? null : extra;
  // The LATEST write per stage, picked by max `id` rather than by array
  // position — a stage can have several step names (EMR = `emr_data_parsed` OR
  // the earlier `emr_gathering` wait) and must resolve to the most recent one,
  // so a gathering failure that a manual fill later recovered doesn't keep
  // showing the stage as failed. Deliberately independent of the response's
  // ordering: relying on it silently inverted this once already.
  const stepForStage = (stage) => {
    // Since MVP-31712 a paused run writes NO standalone `consent_state_check`
    // row — the consent stage resolves through the shared carrier rule (check
    // row wins when present; else the open review pause row, which carries the
    // state on `outcome` and the contact context on `payload`).
    if (stage.key === "consent") return findConsentStateCarrier(steps);
    return maxBy(
      steps.filter((s) => stage.stepNames.includes(s.stepName)),
      "id"
    );
  };

  return (
    <div data-testid="agentic-consent-steps-summary">
      <Text strong style={{ display: "block", marginBottom: 4, fontSize: 12, color: GRAY_600 }}>
        {t("agentic_board.steps_summary.title")}
      </Text>
      {isFetching && !run ? (
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
      ) : (
        <div
          style={{
            position: "relative",
            border: `1px solid ${GRAY_200}`,
            borderRadius: 8,
            padding: "6px 12px"
          }}
        >
          {/* Card-corner action slot (e.g. the source-screenshots button) —
              absolute so it doesn't squeeze the step rows. */}
          {sourceAction && <div style={{ position: "absolute", top: 8, right: 10 }}>{sourceAction}</div>}
          {stages.map((stage) =>
            stage.key === "ee_config_check" ? (
              <EnrollmentSummaryRow
                key={stage.key}
                stage={stage}
                steps={steps.filter((step) => stage.stepNames.includes(step.stepName))}
                reviewingPapId={reviewingPapId}
                t={t}
              />
            ) : (
              <StepSummaryRow key={stage.key} stage={stage} step={stepForStage(stage)} t={t} />
            )
          )}
        </div>
      )}
    </div>
  );
};

// ── Patient contact (live from FN) ──
// The three contact fields eConsent needs — dob / email / phone — read LIVE
// from the FN patient when the modal opens (the parent fetches via
// `getManagePatientDetails`, the GET counterpart to the `setPatientDetailsAPI`
// save, both on `patient/edit/details` — the same details Manage Patient
// Details reads) and pre-filled into the parent-owned `contactForm`. Showing
// what's on file AND letting the navigator fix it in one place. While the fetch
// is in flight the fields are replaced by a skeleton.
//
// The Send gate (in `submitConsentSend`) is: a date of birth AND at least one
// of email/phone — evaluated against the effective values (live + any edits).
// It surfaces here as:
//   • dob `required` → "Date of birth is required" on the dob field,
//   • a form-level `contactChannel` item (validator over both email + phone via
//     `dependencies`) → "Enter at least an email or phone number" below the
//     fields when neither is present.
// Both ride the form, so the handler's single `contactForm.validateFields()`
// catches them and blocks the approve signal until satisfied.
//
// email + phone validate + mask exactly like Manage Patient Details: email runs
// the shared `validateEmail` format check (a typed address must be valid; the
// validator no-ops on empty so the channel-presence rule owns "missing"); phone
// is an `InputNumber` that live-masks to "(123) 456-7890" via
// `formatPhoneNumberWhileTyping`, parses back to bare digits (the patient-edit
// contract is digits-only), and enforces the 10-digit `phoneNumberLengthRegEx`.
const ContactConfirmFields = ({ form, isLoading, dobOnFile, t }) => {
  // Live channel line — recomputed as the navigator edits email/phone. Reads
  // the effective form values (live prefill + edits), so it always reflects
  // what the link will actually go out on.
  const email = Form.useWatch("email", form);
  const phoneNumber = Form.useWatch("phoneNumber", form);
  const channels = [
    email ? contactFieldLabel("email", t) : null,
    phoneNumber ? contactFieldLabel("phoneNumber", t) : null
  ].filter(Boolean);

  return (
    <div data-testid="agentic-consent-contact">
      <Text strong style={{ display: "block", marginBottom: 6, fontSize: 12, color: GRAY_600 }}>
        {t("agentic_board.consent.contact_title")}
      </Text>
      {/* Helper line — spells out the send requirement so the gate isn't a surprise. */}
      <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 11 }}>
        {dobOnFile ? t("agentic_board.consent.contact_note_dob_on_file") : t("agentic_board.consent.contact_note")}
      </Text>
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} title={false} />
      ) : (
        <Form form={form} layout="vertical" requiredMark={false}>
          {/* dob shows only when the patient has none on file. When one exists
              it's hidden (nothing to confirm) and rides along unchanged on Send. */}
          {!dobOnFile && (
            <Form.Item
              name="dob"
              label={contactFieldLabel("dob", t)}
              rules={[{ required: true, message: t("agentic_board.consent.dob_required") }]}
              style={{ marginBottom: 12 }}
            >
              <DatePicker
                format={DATE_FORMAT}
                style={{ width: "100%" }}
                data-testid="agentic-consent-contact-dob"
                placeholder={contactFieldLabel("dob", t)}
              />
            </Form.Item>
          )}
          <Form.Item
            name="email"
            label={contactFieldLabel("email", t)}
            // The shared validator MPD uses — no-ops on empty (the channel rule
            // owns "missing"); a typed address must be a valid email.
            rules={[{ validator: validateEmail, message: t("agentic_board.consent.email_invalid") }]}
            style={{ marginBottom: 12 }}
          >
            <Input data-testid="agentic-consent-contact-email" placeholder={contactFieldLabel("email", t)} />
          </Form.Item>
          <Form.Item
            name="phoneNumber"
            label={contactFieldLabel("phoneNumber", t)}
            // 10-digit length check on the parsed (digits-only) value — the same
            // pattern + message MPD's phone fields use.
            rules={[
              {
                pattern: phoneNumberLengthRegEx,
                message: t("agentic_board.consent.phone_digits")
              }
            ]}
            style={{ marginBottom: 4 }}
          >
            <InputNumber
              controls={false}
              // Keep the value a STRING — without this InputNumber emits a JS
              // number, which gets persisted as a numeric phone_number and
              // crashes FN's normalizePhone on the eConsent send.
              stringMode
              style={{ width: "100%" }}
              maxLength={14}
              placeholder={t("agentic_board.consent.phone_placeholder")}
              formatter={(value) => formatPhoneNumberWhileTyping(value)}
              parser={(value) => value.replace(/[^0-9]/g, "")}
              data-testid="agentic-consent-contact-phone"
            />
          </Form.Item>
          {/* Form-level "at least one channel" gate. A hidden field carrying only
              a cross-field validator (re-runs on email/phone change), so the
              message lands inline under the fields and `validateFields()` blocks
              Send when neither is present. */}
          <ChannelGateItem
            name="contactChannel"
            dependencies={["email", "phoneNumber"]}
            style={{ marginBottom: 0 }}
            rules={[
              ({ getFieldValue }) => ({
                validator() {
                  if (getFieldValue("email") || getFieldValue("phoneNumber")) return Promise.resolve();
                  return Promise.reject(new Error(t("agentic_board.consent.contact_channel_required")));
                }
              })
            ]}
          >
            <Input type="hidden" data-testid="agentic-consent-contact-channel" />
          </ChannelGateItem>
          {channels.length > 0 && (
            <Text
              data-testid="agentic-consent-channels"
              style={{ display: "block", marginTop: 4, fontSize: 12, color: GRAY_600 }}
            >
              {t("agentic_board.consent.channels_will_send", {
                channels: channels.join(", ")
              })}
            </Text>
          )}
        </Form>
      )}
    </div>
  );
};

// ── Consent-state copy panel ──
// The welcoming headline + body for the run's consent state, replacing the raw
// state enum the modal used to show. Two looks, driven only by the state (the
// copy itself carries the meaning):
//   • good-news states (eligible_to_send / contact_info_missing) → a soft green
//     panel + smile icon, so a genuinely positive find doesn't read as a warning,
//   • every edge state (active / in-progress / expired / revoked / link-*) → a
//     calm neutral panel + info icon — informative, never red/alarming.
const ConsentStatePanel = ({ outcome, t }) => {
  const { title, body } = consentStateCopy(outcome, t);
  const goodNews = isGoodNewsOutcome(consentOutcomeKey(outcome));

  return (
    <TaskStatePanel
      icon={goodNews ? SmileOutlined : InfoCircleOutlined}
      title={title}
      body={body}
      goodNews={goodNews}
      data-testid="agentic-consent-state"
    />
  );
};

const ConsentDecideBody = ({
  review,
  patientId,
  contactForm,
  isLoadingContact,
  dobOnFile,
  withFpl,
  onWithFplChange
}) => {
  const { t } = useTranslation();
  const payload = review?.payload || {};
  // Screenshot bytes are PHI — the endpoint 403s non-PHI users, so the button
  // only renders for PHI-cleared callers (same gate as the MRN column).
  const isPhiAllowed = useSelector(selectIsPhiAllowed);
  const [screenshotsOpen, setScreenshotsOpen] = useState(false);
  // `consentOutcome` (UPPERCASE ConsentReason) is the canonical state on the
  // board contract; fall back to the legacy top-level `outcome` for safety.
  // This is the agent's verdict, unrelated to contact — contact is read live.
  const outcome = payload.consentOutcome || review?.outcome;

  return (
    <Space direction="vertical" size={14} style={{ width: "100%" }}>
      {/* Welcoming, human consent-state copy (soft-green for good news, neutral
          for edge states) — never the raw state enum. */}
      <ConsentStatePanel outcome={outcome} t={t} />

      {/* Compact pipeline-steps summary for this run. Its card corner carries
          the source-EMR-screenshots button (the images the extraction read the
          data from — small icon → popup, one image at a time). PHI-gated. */}
      <WorkflowStepsSummary
        patientId={patientId}
        t={t}
        extra={
          isPhiAllowed ? (
            <Tooltip title={t("agentic_board.consent.view_screenshots")}>
              <Button
                size="small"
                icon={<FileImageOutlined />}
                onClick={() => setScreenshotsOpen(true)}
                data-testid="agentic-consent-view-screenshots"
              />
            </Tooltip>
          ) : null
        }
      />
      {isPhiAllowed && (
        <EmrScreenshotsViewer patientId={patientId} open={screenshotsOpen} onClose={() => setScreenshotsOpen(false)} />
      )}

      {/* Patient contact read LIVE from FN — pre-filled + editable dob/email/
          phone, plus the live "Link will be sent via: …" channel line. The Send
          gate (dob + at least one of email/phone) rides this form. */}
      <ContactConfirmFields form={contactForm} isLoading={isLoadingContact} dobOnFile={dobOnFile} t={t} />

      {/* FPL income collection toggle — defaults on when the agent found
          Medicare (willCollectFplDatum). Its value rides along on Send. */}
      <Checkbox
        checked={withFpl}
        onChange={(e) => onWithFplChange(e.target.checked)}
        data-testid="agentic-consent-fpl-checkbox"
      >
        {t("agentic_board.consent.send_with_fpl")}
      </Checkbox>
    </Space>
  );
};

// ─── The modal ─────────────────────────────────────────────────────────────
// One modal; the body + footer switch on `item.hitlTaskType`. emr_fill saves
// the manual EMR data first then resumes; consent_decide reads the patient's
// contact live from FN on open, gates Send on dob + email-or-phone, saves the
// (possibly edited) contact, then resumes. Both flows end on the shared
// `hitlDecision` resume signal (approve for Send/Save & continue, decline
// for Don't-send).

const HitlTaskModal = ({ item, onClose, onResolved, onError }) => {
  const { t } = useTranslation();
  // One form instance for the emr_fill data-entry body.
  const [form] = Form.useForm();
  // A second form for the consent_decide contact fields (dob/email/phone). Owned
  // here so the Send handler can validate + read its values before saving them
  // to the patient, and so the live-fetch prefill below can populate it.
  const [contactForm] = Form.useForm();

  // Consent resume signal. Approve = send the consent, decline = hand back to
  // me. (emr_fill has no separate decision call — its manual save IS the resume,
  // via `postEmrFill` below.)
  const [postConsentDecision, , isPostingConsentDecision] = useApi(ApiNames.AgenticConsentDecision);

  // ee_review: the express-enrollment form the navigator reviews. `eeForm` owns
  // the REAL enrollment form's values (mounted by EeReviewBody), `eePreview`
  // holds what FN seeded it with, and `eeFormErrors` gates Approve — the form's
  // own validation is the gate, exactly as in the manual enroll tab.
  const [eeForm] = Form.useForm();
  const [eePreview, setEePreview] = useState(null);
  const [eeFormErrors, setEeFormErrors] = useState([]);
  const [postEeApprove, , isPostingEeApprove] = useApi(ApiNames.AgenticEeReviewApprove);
  const [postEeDecline, , isPostingEeDecline] = useApi(ApiNames.AgenticEeReviewDecline);
  // emr_fill manual save (precedes the approve signal).
  const [postEmrFill, , isPostingEmrFill] = useApi(ApiNames.AgenticManualEmrFill);
  // consent_decide live patient-contact read. The GET counterpart to the
  // `setPatientDetailsAPI` save below — both hit FN's `patient/edit/details`,
  // the same details Manage Patient Details reads. Fetched when the consent
  // modal opens to pre-fill + display the patient's dob/email/phone.
  const [fetchContact, , isLoadingContact] = useFetch(getManagePatientDetails);
  // consent_decide contact save (precedes the approve signal when the navigator
  // edited the contact fields). Re-wires the FN `patient/edit/details` endpoint
  // Phase 1 unhooked from this modal.
  const [saveContact, , isSavingContact] = useFetch(setPatientDetailsAPI);

  // Snapshot of the contact fetched when the modal opened, so the Send handler
  // can skip the FN patient-edit when the navigator didn't change anything
  // (compare-before-update). Reset per opened card; populated by the fetch below.
  const originalContactRef = useRef(null);

  // Decline-reason capture — consent_decide only.
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  // emr_fill drug picks. Full `rxnormDrug` objects from the RxNorm search —
  // kept here (not in the form) because the picker hands back objects, not a
  // plain field value.
  // Manually-entered medications for emr_fill, mirroring the insurances rows:
  // "Add medication" appends an empty row; each row is a searchable single
  // drug picker + an optional treatment window (start + end) + a remove ✕.
  // `medRowKeyRef` gives every row a stable key (rows start drug-less, so we
  // can't key by the drug). A row left undated submits no date at all — the platform's recency
  // gate does NOT count a dateless med as active, so leaving it blank can cost
  // the med its place in the eligibility check; drug-less rows are dropped on
  // submit. Held here (not in `form`) since the picker hands back full drug
  // objects.
  const medRowKeyRef = useRef(0);
  const [medicationRows, setMedicationRows] = useState([]);
  const handleAddMedicationRow = () =>
    setMedicationRows((prev) => [...prev, { key: medRowKeyRef.current++, drug: null, startDate: null, endDate: null }]);
  const handleMedicationDrugChange = (key, drug) =>
    setMedicationRows((prev) => prev.map((row) => (row.key === key ? { ...row, drug } : row)));
  const handleMedicationStartDateChange = (key, date) =>
    setMedicationRows((prev) => prev.map((row) => (row.key === key ? { ...row, startDate: date } : row)));
  const handleMedicationEndDateChange = (key, date) =>
    setMedicationRows((prev) => prev.map((row) => (row.key === key ? { ...row, endDate: date } : row)));
  const handleRemoveMedicationRow = (key) => setMedicationRows((prev) => prev.filter((row) => row.key !== key));

  // consent_decide "Send with FPL" toggle. Held here (not in the contactForm)
  // because its value rides along on the approve signal, not the patient save.
  // Defaults on when the agent found Medicare (`willCollectFplDatum`); reset
  // from each opened card's payload below.
  const [withFpl, setWithFpl] = useState(false);

  // Whether the FN patient already has a date of birth on file (set from the
  // open-time contact fetch). When true the consent modal hides the dob field —
  // the Send gate's dob requirement is already met by the patient record, so
  // there's nothing for the navigator to confirm or edit.
  const [dobOnFile, setDobOnFile] = useState(false);

  // Reset transient form/decline state whenever a different card opens, and —
  // for the consent task — read the patient's contact LIVE from FN and pre-fill
  // the contact form. `dob` arrives as a `YYYY-MM-DD` string (the patient-edit
  // contract MPD reads); parse it to a moment for the DatePicker, guarding an
  // invalid/empty value. email/phone are strings, set as-is. The fetched item's
  // patientId is captured so a fast re-open with a different card can't prefill
  // the wrong patient.
  useEffect(() => {
    if (!item) return;
    setDeclineOpen(false);
    setDeclineReason("");
    setMedicationRows([]);
    setWithFpl(!!item?.review?.payload?.willCollectFplDatum);
    form.resetFields();
    contactForm.resetFields();
    originalContactRef.current = null;
    setDobOnFile(false);

    if (item.hitlTaskType === HITL_TASK_TYPES.CONSENT_DECIDE && item.patientId != null) {
      const { patientId: openedPatientId } = item;
      fetchContact(openedPatientId).then((res) => {
        // Stale guard: a faster re-open with a different card already swapped
        // `item` — ignore this late response so we don't prefill the wrong patient.
        if (openedPatientId !== item.patientId) return;
        const details = res?.data;
        if (!details) {
          // `useFetch` resolves undefined on error — the prefetch failed. Tell the
          // navigator to enter contact manually instead of leaving a silent blank.
          message.warning(
            t("agentic_board.consent.contact_load_failed", "Couldn't load patient contact — please enter it manually.")
          );
          return;
        }
        const dobValue = details.dob?.value;
        const dobMoment = dobValue ? moment(dobValue, DATE_DATA_FORMAT) : null;
        const normalizedDob = dobMoment && dobMoment.isValid() ? dobMoment.format(DATE_DATA_FORMAT) : null;
        // A dob already on the patient record hides the dob field (nothing to
        // confirm); a missing dob leaves it shown so the navigator can fill it.
        setDobOnFile(!!normalizedDob);
        // Snapshot the fetched contact so the Send handler can skip the FN write
        // when nothing changed (compare-before-update). dob is normalized the
        // SAME way the save serializes it, so an untouched date compares equal.
        originalContactRef.current = {
          dob: normalizedDob,
          email: details.email?.value || null,
          phoneNumber: details.phoneNumber?.value || null
        };
        contactForm.setFieldsValue({
          dob: dobMoment && dobMoment.isValid() ? dobMoment : undefined,
          email: details.email?.value || undefined,
          phoneNumber: details.phoneNumber?.value || undefined
        });
      });
    }
  }, [item]);

  const history = useHistory();

  const open = !!item;
  const taskType = item?.hitlTaskType;
  const patientId = item?.patientId;
  const agenticRunId = item?.agenticRunId;
  const review = item?.review;

  const isBusy =
    isPostingConsentDecision || isPostingEmrFill || isSavingContact || isPostingEeApprove || isPostingEeDecline;

  // consent_decide Send gate (proactive): the eConsent needs a reachable channel,
  // so disable Send when the (editable) contact form has NEITHER email NOR phone.
  // Watched live so it re-enables the moment the navigator types one in. The
  // submit-time `contactForm.validateFields()` still enforces dob + formats.
  const consentEmail = Form.useWatch("email", contactForm);
  const consentPhone = Form.useWatch("phoneNumber", contactForm);
  const hasConsentChannel = Boolean(String(consentEmail ?? "").trim() || String(consentPhone ?? "").trim());
  const consentSendDisabled = taskType === HITL_TASK_TYPES.CONSENT_DECIDE && !hasConsentChannel;

  // Fire the resume signal. Returns the result (undefined on error) so the
  // save-then-resume flows can gate the second call on the first. `extra` carries
  // approve-only decision fields (e.g. `withFpl`) onto the POST body.
  const resume = (decision, reason, extra) =>
    postConsentDecision({ patientId, agenticRunId, decision, ...(reason ? { declineReason: reason } : {}), ...extra });

  // ── emr_fill: save manual EMR → approve ──
  const submitEmrFill = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // validation surfaced inline (incl. a missing plan market)
    }
    // planMarket is required per row, so `validateFields` already guaranteed
    // every row carries one before we get here — it's sent verbatim, never
    // omitted (the backend DTO marks it `@IsEnum(PlanMarket)`, no fallback to
    // pattern-matching). The raw text columns stay optional: the platform
    // tolerates a row that's only a plan market, so there's no empty-row to
    // strip anymore.
    const insurances = (values.insurances || []).map((ins) => ({
      planName: ins.planName || undefined,
      planType: ins.planType || undefined,
      financialClass: ins.financialClass || undefined,
      groupName: ins.groupName || undefined,
      group: ins.group || undefined,
      planMarket: ins.planMarket
    }));
    // Each medication row sends the picked drug's id plus its display name and
    // optional start date — the platform re-fetches the live drug by id, so the
    // derived route flags stay current rather than frozen at pick time.
    // Both ends are serialized to the YYYY-MM-DD (DATE_DATA_FORMAT) contract the
    // platform DTO + extraction share, and each is omitted when left blank — a
    // start-only fill renders as "since <start>", the same as an EMR that
    // records no administration dates.
    const medications = medicationRows
      .filter((row) => row.drug)
      .map((row) => ({
        medicationName: row.drug.tmDisplayName,
        rxnormDrugId: row.drug.id,
        firstTreatmentDate: row.startDate ? row.startDate.format(DATE_DATA_FORMAT) : undefined,
        lastTreatmentDate: row.endDate ? row.endDate.format(DATE_DATA_FORMAT) : undefined
      }));

    // The platform's emr-manual-fill writes the row AND resumes the workflow
    // (the fill IS the resume) — one call, no separate decision signal.
    const fillResult = await postEmrFill({ patientId, agenticRunId, medications, insurances });
    if (fillResult === undefined) {
      onError(t("agentic_board.errors.emr_fill"));
      return;
    }
    resolveWith("agentic_board.resolved.emr_fill");
  };

  // ── consent_decide Send (APPROVE) ──
  // The fast client gate before the approve signal — eConsent needs a date of
  // birth AND at least one of email/phone (the server re-validates on resume;
  // this just keeps the navigator from a click-then-fail). The form carries all
  // three: dob `required`, email/phone format, and the form-level
  // `contactChannel` "at least one" rule — so a single `validateFields()`
  // enforces the gate and surfaces every miss inline. The effective values
  // (live prefill + any edits) live on the form, so this validates exactly
  // what's shown.
  //
  // On a valid Send we SAVE the contact to the FN patient FIRST (so the
  // platform's re-check on resume sees the edits), then APPROVE. dob is
  // serialized to the patient-edit YYYY-MM-DD (DATE_DATA_FORMAT) contract — the
  // same format MPD writes and FN's strict `getDob` parses (an MM/DD/YYYY value
  // would be rejected as invalid and read back as a missing dob); email/phone
  // go through as-is. The "Send with FPL" toggle rides along on the approve.
  const submitConsentSend = async () => {
    // Don't gate on empty fields that simply haven't loaded — the live fetch
    // pre-fills the form, so wait for it before validating.
    if (isLoadingContact) return;

    let values;
    try {
      values = await contactForm.validateFields();
    } catch {
      return; // dob / channel / format miss surfaced inline — don't save or signal
    }

    // `contactChannel` is the hidden gate field — never persisted. Read dob
    // from the form store (not `values`) so an on-file dob — whose field is
    // hidden, so `validateFields` doesn't return it — still rides along
    // unchanged; a navigator-filled dob (missing case) is captured the same way.
    const data = {};
    const dobValue = contactForm.getFieldValue("dob");
    if (dobValue) data.dob = moment(dobValue).format(DATE_DATA_FORMAT);
    if (values.email) data.email = values.email;
    if (values.phoneNumber) data.phoneNumber = getPurePhoneNumber(values.phoneNumber);

    // Only write to the patient when the navigator actually changed a contact
    // field — compare the (possibly edited) form values against the snapshot
    // captured when the modal opened. If dob/email/phone are all untouched, skip
    // the FN write entirely and go straight to the approve signal: no needless
    // patient-edit (nor a spurious PHI-edit audit entry) when the prefilled
    // contact is accepted as-is. The write itself is the SAME FNC→FN
    // `patient/edit/details` call Manage Patient Details uses (the navigator's
    // own session) — never the agentic platform; the platform is signaled only
    // afterwards, on resume.
    const original = originalContactRef.current || {};
    const contactChanged =
      (data.dob ?? null) !== (original.dob ?? null) ||
      (data.email ?? null) !== (original.email ?? null) ||
      (data.phoneNumber ?? null) !== (original.phoneNumber ?? null);

    if (contactChanged) {
      // skipAutomations=true — the agent workflow drives the next step on resume;
      // we don't want the FN patient-edit automations racing it.
      const saveResult = await saveContact(patientId, data, true);
      if (saveResult === undefined) {
        onError(t("agentic_board.errors.save_contact"));
        return;
      }
    }

    const result = await resume("approve", undefined, { withFpl });
    if (result === undefined) {
      onError(t("agentic_board.errors.resume"));
      return;
    }
    resolveWith("agentic_board.resolved.consent_sent");
  };

  // Don't-send (consent_decide DECLINE) — captures a reason first. On success we
  // close the decline sub-modal AND hand control back to the board (onResolved →
  // closes the main modal + refetches); on a resume failure we keep the
  // sub-modal open with the typed reason intact so the navigator can retry.
  /**
   * ee_review APPROVE. Validates the real enrollment form first, then posts the
   * values the navigator SAW AND EDITED — FN creates the application + FILLED
   * form from exactly those and resumes the workflow, which owns the submit. So
   * this resolves as soon as the application exists, not when the program answers.
   */
  const submitEeApprove = async () => {
    let formValues;
    try {
      formValues = await eeForm.validateFields();
    } catch {
      // The form surfaces its own field errors; nothing more to say here.
      return;
    }

    const result = await postEeApprove({
      patientId,
      agenticRunId,
      papId: eePreview?.papId,
      formValues
    });
    // Only real ids mean the application + form were created and the workflow
    // was signaled. `useApi` resolves `undefined` on a failed request and FN
    // answers `null` when the program has no active EE config — neither is a
    // completed approval, and closing the modal on them would show the task as
    // handled while the run sat waiting for a signal that never came.
    if (!result?.applicationId) {
      onError(t("agentic_board.errors.ee_approve"));
      return;
    }
    resolveWith("agentic_board.resolved.ee_approved");
  };

  /**
   * ee_review DECLINE — reason required, same as the consent decline. Nothing was
   * created in FN before approval, so there is nothing to undo: the workflow just
   * hands the patient back to the navigator queue.
   */
  const submitEeDecline = async () => {
    const declineReasonText = declineReason.trim();
    if (!declineReasonText) return;

    const result = await postEeDecline({ patientId, agenticRunId, declineReason: declineReasonText });
    if (result === undefined) {
      onError(t("agentic_board.errors.ee_decline"));
      return;
    }
    setDeclineOpen(false);
    setDeclineReason("");
    resolveWith("agentic_board.resolved.ee_declined");
  };

  const submitDecline = async () => {
    const reason = declineReason.trim();
    if (!reason) return;
    const result = await resume("decline", reason);
    if (result === undefined) {
      onError(t("agentic_board.errors.resume"));
      return;
    }
    setDeclineOpen(false);
    setDeclineReason("");
    resolveWith("agentic_board.resolved.consent_declined");
  };

  // Per-type modal title text, plus a patient-link sub-line under it so the
  // patient reference lives once in the header for every task type (the bodies
  // no longer repeat it). The text renders bare so it keeps the default
  // `.ant-modal-title` bold styling; the link sits below it.
  const isEeReview = taskType === HITL_TASK_TYPES.EE_REVIEW;
  const isPhiAllowed = useSelector(selectIsPhiAllowed);
  const [eeScreenshotsOpen, setEeScreenshotsOpen] = useState(false);
  const titleText = taskType ? t(`agentic_board.modal_title.${taskType}`) : "";
  // ee_review leads with WHAT is being enrolled — `Program (Drug)`, the same
  // shape FNC renders a pap everywhere else. The names are snapshotted onto the
  // pause step when the task is created; without them the task type's title
  // carries it alone.
  const enrollmentName = review?.payload?.programName
    ? `${review.payload.programName}${review.payload.drugName ? ` (${review.payload.drugName})` : ""}`
    : null;
  const title = (
    <>
      {isEeReview && enrollmentName ? (
        <>
          <span style={{ display: "block", fontSize: 12, fontWeight: 400, color: GRAY_600 }}>{titleText}</span>
          {enrollmentName}
        </>
      ) : (
        titleText
      )}
      {patientId != null && (
        <Link
          to={Routes.PATIENT_PATH({ patientId })}
          data-testid="agentic-consent-patient-link"
          style={{ display: "block", fontSize: 13, fontWeight: 400, marginTop: 2 }}
        >
          {/* The feed row carries the PHI-hydrated name — "First Last (id)";
              a non-PHI caller falls back to the bare patient reference. */}
          {item?.patientName ? patientLabel(item) : t("agentic_board.modal_patient", { patientId })}
          <RightOutlined style={{ fontSize: 10, marginLeft: 4 }} />
        </Link>
      )}
    </>
  );

  // Per-type primary action wiring. consent_decide's Send is uniform for every
  // state — it gates on the contact fields, saves the (possibly edited) contact,
  // schedules a follow-up, then signals APPROVE; its tooltip spells the
  // follow-up out (emr_fill has none).
  const primary = (() => {
    switch (taskType) {
      case HITL_TASK_TYPES.EMR_FILL:
        return { label: t("agentic_board.actions.submit_emr"), onClick: submitEmrFill };
      case HITL_TASK_TYPES.CONSENT_DECIDE:
        return {
          label: t("agentic_board.actions.send"),
          tooltip: t("agentic_board.actions.send_tooltip"),
          onClick: submitConsentSend
        };
      case HITL_TASK_TYPES.EE_REVIEW:
        return {
          label: t("agentic_board.ee_review.approve"),
          // The form's own validation gates approval: a disabled button with the
          // reason beats letting them click into a rejection.
          tooltip: eeFormErrors.length
            ? t("agentic_board.ee_review.approve_blocked")
            : t("agentic_board.ee_review.approve_tooltip"),
          disabled: !eePreview || eeFormErrors.length > 0,
          onClick: submitEeApprove
        };
      default:
        return null;
    }
  })();

  // consent_decide also offers Don't-send (DECLINE, reason required).
  // Both navigator DECISIONS offer a decline (reason required); emr_fill doesn't —
  // it's a data entry task, not a decision.
  const canDecline = taskType === HITL_TASK_TYPES.CONSENT_DECIDE || isEeReview;

  // Render the footer as a flex row with an explicit gap rather than a plain
  // array: the Tooltip-wrapped Don't-send / Send buttons aren't adjacent
  // `.ant-btn` siblings, so Ant's default footer spacing skips them and the
  // buttons crowd together. The flex `gap` spaces them uniformly regardless.
  // Every HITL task ends by closing the modal, which on its own leaves the
  // navigator with no confirmation that anything happened — the row simply
  // disappears from the hub. One toast per resolution, worded for the action
  // taken rather than a generic "saved".
  // The toast is the only trace the task leaves behind, so it also carries the
  // way back to the patient the navigator just acted on.
  //
  // `history.push`, not `<Link>`: antd renders the toast into its own detached
  // ReactDOM root, outside the Router, and a `<Link>` there throws. The history
  // object is captured from this component, which IS inside the Router.
  const resolveWith = (messageKey) => {
    const text = t(messageKey);
    message.success({
      duration: RESOLVED_TOAST_DURATION_SECONDS,
      content:
        patientId == null ? (
          text
        ) : (
          <>
            {text}{" "}
            <Typography.Link
              onClick={() => {
                message.destroy();
                history.push(Routes.PATIENT_PATH({ patientId }));
              }}
              data-testid="agentic-board-resolved-patient-link"
            >
              {/* The same patient line the modal header and the review window
                  carry, chevron included — the toast is the third place it
                  appears, and it should read as the same affordance. */}
              {t("agentic_board.modal_patient", { patientId })}
              <RightOutlined style={{ fontSize: 10, marginLeft: 4 }} />
            </Typography.Link>
          </>
        )
    });
    onResolved();
  };

  const gapsLeftLabel =
    isEeReview && eeFormErrors.length ? t("agentic_board.ee_review.fields_left", { count: eeFormErrors.length }) : null;

  const footer = (
    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12 }}>
      {gapsLeftLabel && (
        <Text style={{ marginRight: "auto", fontSize: 12, color: GRAY_600 }} data-testid="agentic-ee-fields-left">
          {gapsLeftLabel}
        </Text>
      )}
      <SecondaryActionButton onClick={onClose} disabled={isBusy} data-testid="agentic-board-modal-cancel">
        {t("agentic_board.actions.cancel")}
      </SecondaryActionButton>
      {canDecline && (
        <Tooltip
          title={t(isEeReview ? "agentic_board.ee_review.decline_tooltip" : "agentic_board.actions.dont_send_tooltip")}
        >
          <DangerActionButton
            onClick={() => setDeclineOpen(true)}
            disabled={isBusy}
            data-testid="agentic-board-modal-decline"
          >
            {t(isEeReview ? "agentic_board.ee_review.decline" : "agentic_board.actions.dont_send")}
          </DangerActionButton>
        </Tooltip>
      )}
      {/* Each task type says whether its own primary is actionable (ee_review
          gates on the form validating); `consentSendDisabled` is consent's own
          reason on top. Reading only the latter silently ignored every other
          type's gate. */}
      {primary &&
        // Wrap in a Tooltip only when the action carries one (consent_decide's Send
        // explains the follow-up); emr_fill's primary stays a bare button. When Send
        // is disabled for want of a contact channel, the tooltip explains why — the
        // <span> wrapper keeps the tooltip hoverable over the disabled button.
        (primary.tooltip ? (
          <Tooltip title={consentSendDisabled ? t("agentic_board.actions.send_disabled_no_contact") : primary.tooltip}>
            <span>
              <ActionButton
                onClick={primary.onClick}
                loading={isBusy}
                disabled={Boolean(primary.disabled) || consentSendDisabled}
                data-testid="agentic-board-modal-submit"
              >
                {primary.label}
              </ActionButton>
            </span>
          </Tooltip>
        ) : (
          <ActionButton
            onClick={primary.onClick}
            loading={isBusy}
            disabled={Boolean(primary.disabled)}
            data-testid="agentic-board-modal-submit"
          >
            {primary.label}
          </ActionButton>
        ))}
    </div>
  );

  const body = (() => {
    switch (taskType) {
      case HITL_TASK_TYPES.EMR_FILL:
        return (
          <EmrFillBody
            form={form}
            medicationRows={medicationRows}
            onAddMedicationRow={handleAddMedicationRow}
            onMedicationDrugChange={handleMedicationDrugChange}
            onMedicationStartDateChange={handleMedicationStartDateChange}
            onMedicationEndDateChange={handleMedicationEndDateChange}
            onRemoveMedicationRow={handleRemoveMedicationRow}
          />
        );
      case HITL_TASK_TYPES.EE_REVIEW:
        return (
          <EeReviewBody
            papId={review?.payload?.papId}
            patientId={patientId}
            form={eeForm}
            onReady={setEePreview}
            onErrors={setEeFormErrors}
            summaryStepCount={EE_SUMMARY_STAGES.length}
            summary={
              /* How the run reached this enrollment. The card corner carries the
                 source-EMR-screenshots button — provenance for what the agent
                 autofilled, PHI-gated exactly as on the consent task. */
              <WorkflowStepsSummary
                patientId={patientId}
                t={t}
                stages={EE_SUMMARY_STAGES}
                reviewingPapId={review?.payload?.papId ?? null}
                extra={
                  isPhiAllowed ? (
                    <Tooltip title={t("agentic_board.consent.view_screenshots")}>
                      <Button
                        size="small"
                        type="text"
                        icon={<PictureOutlined />}
                        onClick={() => setEeScreenshotsOpen(true)}
                        data-testid="agentic-ee-view-screenshots"
                      />
                    </Tooltip>
                  ) : null
                }
              />
            }
          />
        );
      case HITL_TASK_TYPES.CONSENT_DECIDE:
        return (
          <ConsentDecideBody
            review={review}
            patientId={patientId}
            contactForm={contactForm}
            isLoadingContact={isLoadingContact}
            dobOnFile={dobOnFile}
            withFpl={withFpl}
            onWithFplChange={setWithFpl}
          />
        );
      default:
        return null;
    }
  })();

  return (
    <>
      <Modal
        title={title}
        open={open}
        onCancel={onClose}
        footer={footer}
        // 520 fits a decision; the EE review mounts a WHOLE enrollment form
        // (two sections, multi-column rows), so it needs the wide layout too.
        width={taskType === HITL_TASK_TYPES.EMR_FILL || isEeReview ? 720 : 520}
        destroyOnClose
        maskClosable={!isBusy}
        data-testid="agentic-board-modal"
        // The body's reserve has to cover every other band: 24 top + ~55 header
        // + ~53 footer + 24 padding is ~156 of chrome, so 180 leaves slack.
        // Bodies here are unbounded (a full EMR medication list, an enrollment
        // form), which is why the cap exists at all.
        style={{ top: 24 }}
        bodyStyle={{ maxHeight: "calc(100vh - 180px)", overflowY: "auto" }}
      >
        {body}
      </Modal>

      <EmrScreenshotsViewer
        patientId={patientId}
        open={eeScreenshotsOpen}
        onClose={() => setEeScreenshotsOpen(false)}
      />

      {/* Reason capture for the consent_decide Don't-send (DECLINE) flow. */}
      <Modal
        title={t("agentic_board.decline_title")}
        open={declineOpen}
        onOk={isEeReview ? submitEeDecline : submitDecline}
        onCancel={() => !isBusy && setDeclineOpen(false)}
        okText={isEeReview ? t("agentic_board.ee_review.decline") : t("agentic_board.actions.dont_send")}
        okButtonProps={{
          danger: true,
          disabled: !declineReason.trim(),
          loading: isBusy,
          "data-testid": "agentic-board-decline-submit"
        }}
        cancelButtonProps={{ disabled: isBusy }}
        cancelText={t("agentic_board.actions.cancel")}
        destroyOnClose
        data-testid="agentic-board-decline-modal"
      >
        <Text style={{ display: "block", marginBottom: 8, color: GRAY_600, fontSize: 13 }}>
          {t("agentic_board.decline_reason_prompt")}
        </Text>
        <Text
          strong
          style={{ display: "block", marginBottom: 8, fontSize: 13 }}
          data-testid="agentic-board-decline-phi-warning"
        >
          {t("agentic_board.decline_reason_phi_warning")}
        </Text>
        <Input.TextArea
          value={declineReason}
          onChange={(e) => setDeclineReason(e.target.value)}
          rows={4}
          maxLength={500}
          showCount
          placeholder={t("agentic_board.decline_reason_placeholder")}
          data-testid="agentic-board-decline-reason"
          autoFocus
        />
      </Modal>
    </>
  );
};

// `ConsentDecideBody` is exported for a focused unit test of the PHI gate on
// the source-screenshots button (the full modal isn't isolate-renderable —
// portals + live contact fetch); it isn't a public entry point.
export { HitlTaskModal, ConsentDecideBody };
